import ActivityAPI from 'dashboard/api/activities';
import { createStore } from 'dashboard/store/helpers/storeFactory';
import camelcaseKeys from 'camelcase-keys';

export default createStore({
  name: 'Activity',
  API: ActivityAPI,
  getters: {
    getActivitiesList: state => {
      return camelcaseKeys(state.records, { deep: true });
    },
    getActivityById: state => id => {
      const activity = state.records.find(a => a.id === Number(id));
      return activity ? camelcaseKeys(activity, { deep: true }) : null;
    },
    getActivitiesMeta: state => {
      return state.meta || { count: 0, current_page: 1, total_pages: 1 };
    },
    getUIFlags: state => {
      return state.uiFlags || {};
    },
    getPendingTasks: state => {
      return state.records.filter(
        a =>
          ['task', 'call', 'meeting'].includes(a.activity_type) &&
          a.status === 'pending'
      );
    },
    getOverdueTasks: state => {
      const now = new Date();
      return state.records.filter(a => {
        if (!a.due_date || a.status !== 'pending') return false;
        return new Date(a.due_date) < now;
      });
    },
    getActivitiesByType: state => type => {
      return state.records.filter(a => a.activity_type === type);
    },
    getActivitiesForObject: state => (type, id) => {
      return state.records.filter(
        a => a.activitable_type === type && a.activitable_id === Number(id)
      );
    },
    getTodayActivities: state => {
      return state.todayActivities || [];
    },
    getUpcomingActivities: state => {
      return state.upcomingActivities || [];
    },
    getSummary: state => {
      return (
        state.summary || { total: 0, completed: 0, pending: 0, overdue: 0 }
      );
    },
    getActivitiesForDeal: state => dealId => {
      return state.records.filter(
        a => a.conversation_funnel_mapping_id === Number(dealId)
      );
    },
  },
  mutations: {
    SET_PENDING_TASKS(state, tasks) {
      state.pendingTasks = tasks;
    },
    SET_OVERDUE_TASKS(state, tasks) {
      state.overdueTasks = tasks;
    },
    SET_TODAY_ACTIVITIES(state, activities) {
      state.todayActivities = activities;
    },
    SET_UPCOMING_ACTIVITIES(state, activities) {
      state.upcomingActivities = activities;
    },
    SET_SUMMARY(state, summary) {
      state.summary = summary;
    },
  },
  actions: mutationTypes => ({
    fetchFiltered: async ({ commit }, filters = {}) => {
      commit(mutationTypes.SET_UI_FLAG, { fetchingList: true });
      try {
        const {
          data: { payload, meta },
        } = await ActivityAPI.get(filters);
        commit(mutationTypes.SET, payload);
        commit(mutationTypes.SET_META, meta);
        return payload;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { fetchingList: false });
      }
    },
    getActivity: async ({ commit }, id) => {
      commit(mutationTypes.SET_UI_FLAG, { fetching: true });
      try {
        const { data } = await ActivityAPI.getActivity(id);
        commit(mutationTypes.ADD, data);
        return data;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { fetching: false });
      }
    },
    createActivity: async ({ commit }, activityData) => {
      commit(mutationTypes.SET_UI_FLAG, { creating: true });
      try {
        const { data } = await ActivityAPI.create(activityData);
        commit(mutationTypes.ADD, data);
        return data;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { creating: false });
      }
    },
    updateActivity: async ({ commit }, { id, ...activityData }) => {
      commit(mutationTypes.SET_UI_FLAG, { updating: true });
      try {
        const { data } = await ActivityAPI.update(id, activityData);
        commit(mutationTypes.EDIT, data);
        return data;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { updating: false });
      }
    },
    deleteActivity: async ({ commit }, id) => {
      commit(mutationTypes.SET_UI_FLAG, { deleting: true });
      try {
        await ActivityAPI.delete(id);
        commit(mutationTypes.DESTROY, id);
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { deleting: false });
      }
    },
    complete: async ({ commit }, id) => {
      const { data } = await ActivityAPI.complete(id);
      commit(mutationTypes.EDIT, data);
      return data;
    },
    cancel: async ({ commit }, id) => {
      const { data } = await ActivityAPI.cancel(id);
      commit(mutationTypes.EDIT, data);
      return data;
    },
    fetchPendingTasks: async ({ commit }, page = 1) => {
      commit(mutationTypes.SET_UI_FLAG, { fetchingList: true });
      try {
        const {
          data: { payload, meta },
        } = await ActivityAPI.getPendingTasks(page);
        commit('SET_PENDING_TASKS', payload);
        commit(mutationTypes.SET_META, meta);
        return payload;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { fetchingList: false });
      }
    },
    fetchOverdueTasks: async ({ commit }, page = 1) => {
      commit(mutationTypes.SET_UI_FLAG, { fetchingList: true });
      try {
        const {
          data: { payload, meta },
        } = await ActivityAPI.getOverdueTasks(page);
        commit('SET_OVERDUE_TASKS', payload);
        commit(mutationTypes.SET_META, meta);
        return payload;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { fetchingList: false });
      }
    },
    forCompany: async ({ commit }, { companyId, page = 1 }) => {
      commit(mutationTypes.SET_UI_FLAG, { fetchingList: true });
      try {
        const {
          data: { payload, meta },
        } = await ActivityAPI.forCompany(companyId, page);
        commit(mutationTypes.SET, payload);
        commit(mutationTypes.SET_META, meta);
        return payload;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { fetchingList: false });
      }
    },
    forDeal: async ({ commit }, { dealId, page = 1 }) => {
      commit(mutationTypes.SET_UI_FLAG, { fetchingList: true });
      try {
        const {
          data: { payload, meta },
        } = await ActivityAPI.forDeal(dealId, page);
        commit(mutationTypes.SET, payload);
        commit(mutationTypes.SET_META, meta);
        return payload;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { fetchingList: false });
      }
    },
    forContact: async ({ commit }, { contactId, page = 1 }) => {
      commit(mutationTypes.SET_UI_FLAG, { fetchingList: true });
      try {
        const {
          data: { payload, meta },
        } = await ActivityAPI.forContact(contactId, page);
        commit(mutationTypes.SET, payload);
        commit(mutationTypes.SET_META, meta);
        return payload;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { fetchingList: false });
      }
    },
    fetchToday: async ({ commit }) => {
      commit(mutationTypes.SET_UI_FLAG, { fetchingList: true });
      try {
        const { data } = await ActivityAPI.getToday();
        commit('SET_TODAY_ACTIVITIES', data.payload);
        if (data.summary) {
          commit('SET_SUMMARY', data.summary);
        }
        return data.payload;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { fetchingList: false });
      }
    },
    fetchOverdue: async ({ commit }) => {
      commit(mutationTypes.SET_UI_FLAG, { fetchingList: true });
      try {
        const { data } = await ActivityAPI.getOverdue();
        commit('SET_OVERDUE_TASKS', data.payload);
        return data.payload;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { fetchingList: false });
      }
    },
    fetchUpcoming: async ({ commit }, days = 7) => {
      commit(mutationTypes.SET_UI_FLAG, { fetchingList: true });
      try {
        const { data } = await ActivityAPI.getUpcoming(days);
        commit('SET_UPCOMING_ACTIVITIES', data.payload);
        return data.payload;
      } finally {
        commit(mutationTypes.SET_UI_FLAG, { fetchingList: false });
      }
    },
    fetchSummary: async ({ commit }, { userId, date } = {}) => {
      try {
        const { data } = await ActivityAPI.getSummary(userId, date);
        commit('SET_SUMMARY', data);
        return data;
      } catch (error) {
        return null;
      }
    },
    reschedule: async ({ commit }, { id, dueAt }) => {
      const { data } = await ActivityAPI.reschedule(id, dueAt);
      commit(mutationTypes.EDIT, data);
      return data;
    },
    bulkComplete: async ({ commit }, { activityIds, outcome, notes }) => {
      const { data } = await ActivityAPI.bulkComplete(
        activityIds,
        outcome,
        notes
      );
      return data;
    },
  }),
});
